/**
 * The widget-side half of actions.
 *
 * Every data widget needs the same three things — turn incoming signals into a
 * query constraint, know what to show in the "filtered by…" chip, and know how
 * to clear it — so they live here rather than being repeated in each renderer.
 */

import { useCallback, useMemo } from 'react';
import { buildConstraint, combineWhere, type IncomingConstraint } from '../utils/actionRuntime';
import { fetchMatchingGeometry, mergeGeometry } from '../services/dashboardDataService';
import type {
  ActionSignal,
  DashboardDataSource,
  DashboardField,
  DashboardWidget,
} from '../types/dashboardTypes';

/** Props every widget that can take part in actions accepts. */
export interface ActionTargetProps {
  /** Constraints other widgets are imposing on this one. */
  incomingSignals?: ActionSignal[];
  /** Dismisses those constraints at their source. */
  onClearSignals?: (sourceIds: string[]) => void;
}

/** Extra props for widgets that can also RAISE a signal. */
export interface ActionSourceProps {
  /** What this widget is currently broadcasting, so it can highlight it. */
  activeSignal?: ActionSignal | null;
  onSignal?: (sourceId: string, signal: ActionSignal | null) => void;
}

export function useIncomingConstraint(
  signals: ActionSignal[] | undefined,
  fields: DashboardField[],
): IncomingConstraint {
  const key = JSON.stringify(signals ?? []);
  return useMemo(
    () => buildConstraint(signals ?? [], fields),
    // Signals arrive as a fresh array every render; comparing the serialised
    // form keeps the constraint stable and stops a refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, fields],
  );
}

export { combineWhere };

/**
 * Wraps a widget's `onSignal` so a selection carries whatever its targets need.
 *
 * Two things can be missing from a raw selection, and both are the same kind of
 * problem — the source knows the clicked VALUE, but a target may need
 * something the source widget never displayed:
 *
 *  - a SHAPE, when a target matches by location
 *  - the value of ANOTHER COLUMN, when a target compares against a field the
 *    source is not grouped by — a chart showing ward names driving a pipe
 *    layer keyed by ward code
 *
 * Both are resolved HERE, at the source, and both come out of ONE request for
 * the selected rows. Doing it per target would repeat that request once per
 * listener, and a target has no way to ask "what did the source select"
 * without re-running the source's own query.
 *
 * Nothing is fetched unless an action actually asks. A board whose actions all
 * match on the displayed field costs exactly what it did before.
 */
export function useOutgoingSignal(
  widget: DashboardWidget,
  source: DashboardDataSource | undefined,
  onSignal: ((sourceId: string, signal: ActionSignal | null) => void) | undefined,
  /** The widget's own filter, so the lookup sees what is actually on screen. */
  ownWhere: string,
) {
  const actions = useMemo(() => widget.actions ?? [], [widget.actions]);

  // Only a filter can match spatially; a map highlight is always by attribute.
  const wantsShape = actions.some(
    (action) => action.kind === 'filter' && action.match === 'spatial',
  );

  /* Source columns some target wants to be matched on. Every kind counts, not
     just `filter` — a map highlighting by a paired column needs the same
     lookup a chart does. Deduplicated, since several targets keying off the
     same code should cost one request, not one each. */
  const pairedFields = useMemo(() => {
    const names = new Set<string>();
    for (const action of actions) {
      if (action.match === 'spatial') continue;
      for (const pair of Object.values(action.fieldMap ?? {})) {
        if (pair?.source) names.add(pair.source);
      }
    }
    return [...names];
  }, [actions]);

  const pairedKey = pairedFields.join('|');

  return useCallback(
    (sourceId: string, signal: ActionSignal | null) => {
      // Columns the source widget does not itself broadcast have to be read.
      const missing =
        signal && signal.kind === 'selection'
          ? pairedFields.filter((name) => name !== signal.field)
          : [];

      if (!signal || signal.kind !== 'selection' || !source || (!wantsShape && missing.length === 0)) {
        onSignal?.(sourceId, signal);
        return;
      }

      /* Broadcast immediately WITHOUT the extras, then again with them. The
         targets show their loading state on the first call rather than sitting
         unchanged until the request returns, which on a slow service reads as
         a click that did nothing. */
      onSignal?.(sourceId, signal);

      // A multi-select source hands over `values` (see ActionSignal's own
      // note); everything else still selects one row at a time and `value`
      // alone is the whole story.
      const pickedValues = signal.values && signal.values.length > 0 ? signal.values : [signal.value];
      const blankValues = pickedValues.filter((entry) => !entry);
      const presentValues = pickedValues.filter((entry) => Boolean(entry));
      const literal = (entry: string) => `'${entry.replace(/'/g, "''")}'`;
      const clauseParts: string[] = [];
      if (presentValues.length === 1) clauseParts.push(`${signal.field} = ${literal(presentValues[0])}`);
      else if (presentValues.length > 1) {
        clauseParts.push(`${signal.field} IN (${presentValues.map(literal).join(', ')})`);
      }
      if (blankValues.length > 0) clauseParts.push(`${signal.field} IS NULL`);
      const clause =
        clauseParts.length === 0
          ? `${signal.field} IS NULL`
          : clauseParts.length === 1
            ? clauseParts[0]
            : `(${clauseParts.join(' OR ')})`;
      const where = combineWhere(ownWhere, clause);

      void fetchMatchingGeometry(source, where, { includeGeometry: wantsShape })
        .then((collection) => {
          if (!collection) return;

          /* ALL the matching shapes, merged. One value routinely matches
             several rows — a ward drawn in three pieces, every pipe on a
             street — and filtering by the first one alone would return a
             fraction of the right answer while still looking reasonable. */
          const geometry = wantsShape ? mergeGeometry(collection) : null;

          const fieldValues: Record<string, string[]> = {};
          for (const name of missing) {
            const seen = new Set<string>();
            for (const feature of collection.features ?? []) {
              const raw = (feature?.properties as Record<string, unknown> | undefined)?.[name];
              // null and undefined are the blank bucket, which the runtime
              // turns into IS NULL; every other value compares as text.
              seen.add(raw === null || raw === undefined ? '' : String(raw));
            }
            // An empty list would read as "not resolved yet" downstream, so a
            // column that genuinely holds nothing is recorded as one blank.
            fieldValues[name] = seen.size > 0 ? [...seen] : [''];
          }

          if (!geometry && missing.length === 0) return;
          onSignal?.(sourceId, {
            ...signal,
            ...(geometry ? { geometry } : null),
            ...(missing.length > 0 ? { fieldValues } : null),
          });
        })
        .catch(() => undefined);
    },
    // `pairedKey` stands in for the array, which is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wantsShape, pairedKey, source, onSignal, ownWhere],
  );
}
